import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '@/components/ui/button';
import {
  FileText, CheckCircle, Clock, TrendingUp, AlertTriangle,
  ChevronRight, CreditCard, XCircle, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const fmtDate = (d) => d ? format(new Date(d), 'dd/MM/yyyy', { locale: it }) : '—';

const STATI_LABELS = {
  in_attesa: 'In Attesa', presa_in_carico: 'Presa in Carico',
  richiesta_pagamento: 'Rich. Pagamento', pagamento_posticipato: 'Pag. Posticipato',
  pagamento_allegato: 'Pag. Allegato', pagamento_accettato: 'Pag. Accettato',
  incasso_inviato: 'Incasso Inviato', emessa_da_pagare: 'Emessa - Da Pagare',
  emessa: 'Emessa', rifiutata: 'Rifiutata',
};
const STATI_COLORS = {
  in_attesa: '#F59E0B', presa_in_carico: '#3B82F6', richiesta_pagamento: '#A855F7',
  pagamento_posticipato: '#D97706', pagamento_allegato: '#8B5CF6', pagamento_accettato: '#7C3AED',
  incasso_inviato: '#F97316', emessa_da_pagare: '#E11D48', emessa: '#10B981', rifiutata: '#EF4444',
};

const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5" data-testid={`stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className={color || 'text-slate-400'} />
      <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl sm:text-3xl font-bold ${color || 'text-slate-900'}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </div>
);

const StatoBadge = ({ stato }) => (
  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{
    backgroundColor: (STATI_COLORS[stato] || '#94A3B8') + '20',
    color: STATI_COLORS[stato] || '#94A3B8'
  }}>
    {STATI_LABELS[stato] || stato}
  </span>
);

const MONTH_NAMES = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return `${MONTH_NAMES[parseInt(mo) - 1]} ${y.slice(2)}`;
};

export default function MyDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/richieste-emissione/my-dashboard');
      setData(res.data);
    } catch { toast.error('Errore caricamento dashboard'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-10 w-10 border-b-2 border-slate-600 rounded-full" /></div>;
  if (!data) return <div className="p-8 text-center text-slate-500">Errore caricamento dati</div>;

  const chartData = (data.monthly || []).map(m => ({
    mese: formatMonth(m.mese),
    Richieste: m.richieste,
    Emesse: m.emesse,
    Premi: m.premi,
  }));

  return (
    <div className="p-3 sm:p-6 md:p-8" data-testid="my-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">La Mia Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Riepilogo della tua produzione</p>
        </div>
        <Button onClick={() => navigate('/richieste-emissione')} variant="outline" size="sm" data-testid="btn-vai-richieste">
          <FileText size={14} className="mr-1.5" /> Le Mie Richieste
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard icon={FileText} label="Totale Richieste" value={data.totale} />
        <StatCard icon={CheckCircle} label="Polizze Emesse" value={data.stati?.emessa || 0} color="text-green-600" />
        <StatCard icon={Clock} label="In Lavorazione" value={(data.stati?.presa_in_carico || 0) + (data.stati?.incasso_inviato || 0) + (data.stati?.pagamento_accettato || 0)} color="text-blue-600" />
        <StatCard icon={TrendingUp} label="Conversione" value={`${data.tasso_conversione}%`} color={data.tasso_conversione >= 50 ? 'text-green-600' : 'text-amber-600'} />
        <StatCard icon={CreditCard} label="Premi Emessi" value={`€ ${data.premi_emessi.toLocaleString('it-IT', { minimumFractionDigits: 0 })}`} color="text-slate-900" />
        <StatCard icon={AlertTriangle} label="Da Saldare" value={data.sospesi} color="text-red-600" sub={data.sospesi_importo > 0 ? `€ ${data.sospesi_importo.toLocaleString('it-IT', { minimumFractionDigits: 0 })}` : null} />
      </div>

      {/* Chart + Sospesi */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Production Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-slate-600" />
            <h2 className="text-sm font-semibold text-slate-800">Produzione Mensile</h2>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="mese" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                  formatter={(value, name) => [name === 'Premi' ? `€ ${value.toLocaleString('it-IT')}` : value, name]}
                />
                <Bar dataKey="Richieste" fill="#93C5FD" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Emesse" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">Nessun dato disponibile</div>
          )}
        </div>

        {/* Sospesi */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-red-500" />
            <h2 className="text-sm font-semibold text-slate-800">Polizze Da Saldare</h2>
            {data.sospesi > 0 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold ml-auto">{data.sospesi}</span>
            )}
          </div>
          {data.sospesi_list?.length > 0 ? (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {data.sospesi_list.map(s => (
                <button key={s.id} onClick={() => navigate('/richieste-emissione')}
                  className="w-full text-left p-3 rounded-lg bg-red-50 border border-red-100 hover:bg-red-100 transition-colors" data-testid={`sospeso-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-900">{s.cliente}</span>
                    <span className="text-xs font-bold text-red-600">{s.premio ? `€ ${s.premio}` : ''}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">{s.compagnia}{s.targa ? ` · ${s.targa}` : ''}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[240px] text-center">
              <CheckCircle size={40} className="text-green-300 mb-3" />
              <p className="text-sm font-semibold text-green-700">Tutto saldato!</p>
              <p className="text-xs text-slate-400 mt-1">Nessuna polizza in sospeso</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Requests */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800">Ultime Richieste</h2>
          <Button size="sm" variant="ghost" onClick={() => navigate('/richieste-emissione')} className="text-xs text-blue-600" data-testid="btn-vedi-tutte">
            Vedi tutte <ChevronRight size={14} className="ml-0.5" />
          </Button>
        </div>
        <div className="divide-y divide-slate-50">
          {data.recenti?.length > 0 ? data.recenti.map(r => (
            <div key={r.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50/50 transition-colors cursor-pointer"
              onClick={() => navigate('/richieste-emissione')} data-testid={`recente-${r.id}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: (STATI_COLORS[r.stato] || '#94A3B8') + '15' }}>
                <FileText size={16} style={{ color: STATI_COLORS[r.stato] || '#94A3B8' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{r.cliente}</span>
                  <StatoBadge stato={r.stato} />
                </div>
                <p className="text-xs text-slate-500 mt-0.5 truncate">
                  {r.compagnia} · {r.tipo_polizza}{r.targa ? ` · ${r.targa}` : ''}{r.premio ? ` · € ${r.premio}` : ''}
                </p>
              </div>
              <p className="text-[11px] text-slate-400 flex-shrink-0 hidden sm:block">{fmtDate(r.created_at)}</p>
              <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
            </div>
          )) : (
            <div className="text-center py-12"><p className="text-sm text-slate-400">Nessuna richiesta</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
